import { useQuery } from "@tanstack/react-query";
import { collection, getDocs } from "firebase/firestore";
import { db } from "@/services/firebase";
import { converter } from "@/lib/firestore-converters";
import { categories as seedCategories } from "@/data";
import type { Category } from "@/types";
import { useProducts } from "./use-products";

const USE_SEED = import.meta.env.VITE_USE_SEED_DATA === "true";

export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: async (): Promise<Category[]> => {
      if (USE_SEED)
        return [...seedCategories].sort((a, b) => a.order - b.order);
      const col = collection(db, "categories").withConverter(
        converter<Category>(),
      );
      return (await getDocs(col)).docs
        .map((d) => d.data())
        .sort((a, b) => a.order - b.order);
    },
    staleTime: 30 * 60_000,
  });
}

export function useCategory(slug: string) {
  const { data, ...rest } = useCategories();
  return { data: data?.find((c) => c.slug === slug), ...rest };
}

/**
 * DRB-72 — categories that actually have something to sell.
 *
 * 6 of the 8 categories are empty and render "Coming soon". The sitemap now omits them and the
 * route sets robots=noindex while empty, but the nav, the mobile nav and Shop by Condition all
 * linked EVERY category — so Google reached them anyway by crawling our own links, and a customer
 * clicking "Sleep & Nighttime" landed on a dead end.
 *
 * One hook rather than the same filter written into three components, which is how the head tags
 * ended up duplicated across ten routes.
 *
 * While products are still loading this returns the FULL list, deliberately: hiding a real
 * category because a fetch is in flight is worse than briefly showing one that turns out to be
 * empty, and `useProducts()` is react-query-cached (5 min) and already warm on most navigations.
 */
export function usePopulatedCategories() {
  const { data: categories = [], ...rest } = useCategories();
  const { data: products, isLoading: productsLoading } = useProducts();

  if (productsLoading || !products) return { data: categories, ...rest };

  const populated = new Set(products.map((p) => p.category).filter(Boolean));
  return { data: categories.filter((c) => populated.has(c.slug)), ...rest };
}
